import { TransformControls } from '@react-three/drei';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type Euler, type Object3D, Quaternion } from 'three';

import { usePlayerCameraControls, usePlayerStoreApi } from '@cartwhl/3d/player';

import { KEY_POSE_TRANSFORM_CONTROLS_SIZE } from '../constants';
import { solveKeyPoseFkRotation } from '../fk/keyPoseFkSystem';
import { useControlFrameAutoReconcile } from '../hooks/useControlFrameAutoReconcile';
import { useKeyPoseFrameCaptureActions } from '../hooks/useKeyPoseCaptureActions';
import { useKeyPoseFkEditing } from '../hooks/useKeyPoseFkEditing';
import { useKeyPosePersistenceActions } from '../hooks/useKeyPosePersistenceActions';
import {
  createEditableKeyPoseScene,
  getKeyPoseAnkleWorldPositions,
  getKeyPoseIkEffectorWorldPosition,
} from '../ik/keyPoseIkSystem';
import {
  clearKeyPoseDragPreview,
  setKeyPoseDragPreview,
} from '../keyPoseDragPreview';
import {
  useStore as useKeyPoseStore,
  useStoreApi as useKeyPoseStoreApi,
} from '../keyPoseStore';
import { createKeyPoseTimingLogger } from '../keyPoseTiming';
import type {
  KeyPoseEditingPreviewRecord,
  KeyPoseId,
  KeyPoseIkEffectorId,
  KeyPoseIkTargetPositions,
  KeyPoseQuaternionTuple,
  KeyPoseRecord,
  KeyPoseVector3Tuple,
} from '../types';

interface KeyPoseFkHandlesProps {
  sceneRotation?: Euler;
  onHandleDragStart?: () => void;
}

interface KeyPoseFkShadowTransform {
  position: KeyPoseVector3Tuple;
  quaternion: KeyPoseQuaternionTuple;
}

const GIZMO_POSITION_EPSILON = 1e-7;
const GIZMO_ROTATION_EPSILON = 1e-5;

export default function KeyPoseFkHandles({
  sceneRotation,
  onHandleDragStart,
}: KeyPoseFkHandlesProps) {
  const keyPoseStore = useKeyPoseStore();
  const keyPoseStoreApi = useKeyPoseStoreApi();
  const playerStore = usePlayerStoreApi();
  const { cameraControls } = usePlayerCameraControls();
  const { saveCurrentKeyPoses } = useKeyPosePersistenceActions();
  const { ensureEditableKeyPoseAtCurrentFrame } =
    useKeyPoseFrameCaptureActions();
  const { reconcileControlFramesAfterEdit } = useControlFrameAutoReconcile();
  const {
    actorScene,
    handleTransform,
    keyPoseIsTransient,
    lockFeet,
    jointId,
    keyPose,
    sourceBvh,
  } = useKeyPoseFkEditing({ sceneRotation });
  const handleRef = useRef<Object3D | null>(null);
  const latestSolvedRef = useRef<KeyPoseEditingPreviewRecord | null>(null);
  const pendingBaseKeyPoseRef = useRef<KeyPoseRecord | null>(null);
  const pendingStartTransformRef = useRef<KeyPoseFkShadowTransform | null>(
    null,
  );
  const pendingCameraEnabledRef = useRef<boolean | null>(null);
  const dragBaseKeyPoseRef = useRef<KeyPoseRecord | null>(null);
  const dragStartQuaternionRef = useRef<Quaternion | null>(null);
  const dragPreviousTargetQuaternionRef = useRef<Quaternion | null>(null);
  const dragPinnedAnklePositionsRef = useRef<KeyPoseIkTargetPositions | null>(
    null,
  );
  const dragCreatedKeyPoseRef = useRef(false);
  const previousKeyPoseIdRef = useRef<string | null>(null);
  const isDraggingRef = useRef(false);
  const editableSceneRef = useRef<Object3D | null>(null);
  const [handleObject, setHandleObject] = useState<Object3D | null>(null);
  const [shadowTransform, setShadowTransform] =
    useState<KeyPoseFkShadowTransform | null>(null);

  const setDragging = useCallback(
    (dragging: boolean, keyPoseId: KeyPoseId | null = null) => {
      isDraggingRef.current = dragging;
      keyPoseStore.setKeyPoseEditingDragging(dragging, keyPoseId);
      playerStore.setGizmoDragging(dragging);
    },
    [keyPoseStore, playerStore],
  );

  const setHandleRefs = useCallback((object: Object3D | null) => {
    handleRef.current = object;
    setHandleObject(object);
  }, []);

  const disableCameraControlsForPendingHandle = useCallback(() => {
    pendingCameraEnabledRef.current = null;
    if (!cameraControls) {
      return;
    }

    if ('enabled' in cameraControls) {
      pendingCameraEnabledRef.current = (
        cameraControls as { enabled: boolean }
      ).enabled;
      (cameraControls as { enabled: boolean }).enabled = false;
    }
    cameraControls.cancel?.();
    cameraControls.stop?.();
  }, [cameraControls]);

  const releasePendingHandleCamera = useCallback(() => {
    const previousCameraEnabled = pendingCameraEnabledRef.current;
    if (previousCameraEnabled === null) {
      return;
    }

    pendingCameraEnabledRef.current = null;
    if (!cameraControls || !('enabled' in cameraControls)) {
      return;
    }

    cameraControls.stop?.();
    (cameraControls as { enabled: boolean }).enabled = previousCameraEnabled;
  }, [cameraControls]);

  const clearPendingHandleDrag = useCallback(() => {
    pendingBaseKeyPoseRef.current = null;
    pendingStartTransformRef.current = null;
  }, []);

  useEffect(() => {
    const previousKeyPoseId = previousKeyPoseIdRef.current;
    const nextKeyPoseId = keyPose?.id ?? null;
    previousKeyPoseIdRef.current = nextKeyPoseId;

    if (previousKeyPoseId && previousKeyPoseId !== nextKeyPoseId) {
      keyPoseStore.clearKeyPoseEditingPreview(previousKeyPoseId);
    }
  }, [keyPose?.id, keyPoseStore]);

  useEffect(() => {
    if (isDraggingRef.current) {
      return;
    }

    setShadowTransform(
      handleTransform
        ? {
            position: clonePosition(handleTransform.position),
            quaternion: cloneQuaternionTuple(handleTransform.quaternion),
          }
        : null,
    );
    latestSolvedRef.current = null;
  }, [handleTransform, jointId, keyPose?.id]);

  useEffect(() => {
    if (!handleRef.current || !shadowTransform) {
      return;
    }

    applyShadowTransform(handleRef.current, shadowTransform);
  }, [shadowTransform]);

  useEffect(() => {
    return () => {
      const hadPendingHandleDrag = pendingBaseKeyPoseRef.current !== null;
      clearPendingHandleDrag();
      if (hadPendingHandleDrag && !isDraggingRef.current) {
        releasePendingHandleCamera();
      }
      if (isDraggingRef.current) {
        setDragging(false);
      }
    };
  }, [clearPendingHandleDrag, releasePendingHandleCamera, setDragging]);

  const solveAtQuaternion = useCallback(
    (nextQuaternion: Quaternion) => {
      const baseKeyPose = dragBaseKeyPoseRef.current ?? keyPose;
      const startQuaternion = dragStartQuaternionRef.current;
      if (!baseKeyPose || !jointId || !startQuaternion) {
        return null;
      }

      const solved = solveKeyPoseFkRotation({
        actorScene,
        bvh: sourceBvh,
        keyPose: baseKeyPose,
        sceneRotation,
        jointId,
        startWorldQuaternion: startQuaternion,
        targetWorldQuaternion: nextQuaternion,
        editableScene: editableSceneRef.current,
        previousTargetWorldQuaternion: dragPreviousTargetQuaternionRef.current,
        lockFeet,
        pinnedAnklePositions: dragPinnedAnklePositionsRef.current,
      });
      if (!solved) {
        return null;
      }

      dragPreviousTargetQuaternionRef.current = nextQuaternion.clone();
      latestSolvedRef.current = solved;
      if (isDraggingRef.current) {
        setKeyPoseDragPreview(baseKeyPose.id, solved);
        return solved;
      }

      keyPoseStore.setKeyPoseEditingPreview({
        keyPoseId: baseKeyPose.id,
        ...solved,
      });

      return solved;
    },
    [
      actorScene,
      lockFeet,
      jointId,
      keyPose,
      keyPoseStore,
      sceneRotation,
      sourceBvh,
    ],
  );

  const startPendingDrag = useCallback(() => {
    if (!keyPose || !jointId || !shadowTransform) {
      return;
    }

    pendingBaseKeyPoseRef.current = keyPose;
    pendingStartTransformRef.current = {
      position: clonePosition(shadowTransform.position),
      quaternion: cloneQuaternionTuple(shadowTransform.quaternion),
    };
    latestSolvedRef.current = null;
    disableCameraControlsForPendingHandle();
  }, [
    jointId,
    keyPose,
    disableCameraControlsForPendingHandle,
    shadowTransform,
  ]);

  const ensureDragStarted = useCallback(
    (nextTransform: KeyPoseFkShadowTransform) => {
      if (isDraggingRef.current) {
        return true;
      }

      let baseKeyPose = pendingBaseKeyPoseRef.current;
      const startTransform = pendingStartTransformRef.current;
      if (
        !baseKeyPose ||
        !jointId ||
        !startTransform ||
        areFkTransformsEqual(startTransform, nextTransform)
      ) {
        return false;
      }

      dragCreatedKeyPoseRef.current = false;
      if (keyPoseIsTransient) {
        const result = ensureEditableKeyPoseAtCurrentFrame();
        if (!result) {
          return false;
        }

        const persistedKeyPose =
          keyPoseStoreApi.state.keyPosesById[result.keyPoseId];
        if (!persistedKeyPose) {
          return false;
        }

        baseKeyPose = persistedKeyPose;
        dragCreatedKeyPoseRef.current = result.created;
      }

      if (cameraControls) {
        playerStore.saveGizmoCameraSnapshot(cameraControls);
        if ('enabled' in cameraControls) {
          (cameraControls as { enabled: boolean }).enabled = false;
        }
        cameraControls.cancel?.();
        cameraControls.stop?.();
      }

      setDragging(true, baseKeyPose.id);
      onHandleDragStart?.();

      dragBaseKeyPoseRef.current = baseKeyPose;
      const startQuaternion = quaternionFromTuple(startTransform.quaternion);
      dragStartQuaternionRef.current = startQuaternion;
      dragPreviousTargetQuaternionRef.current = startQuaternion.clone();
      latestSolvedRef.current = null;
      editableSceneRef.current = createEditableKeyPoseScene({
        actorScene,
        keyPose: baseKeyPose,
        sceneRotation,
      });
      dragPinnedAnklePositionsRef.current =
        lockFeet && editableSceneRef.current
          ? getKeyPoseAnkleWorldPositions(editableSceneRef.current)
          : null;

      clearPendingHandleDrag();
      pendingCameraEnabledRef.current = null;
      return true;
    },
    [
      actorScene,
      cameraControls,
      clearPendingHandleDrag,
      lockFeet,
      jointId,
      ensureEditableKeyPoseAtCurrentFrame,
      keyPoseIsTransient,
      keyPoseStoreApi,
      onHandleDragStart,
      playerStore,
      sceneRotation,
      setDragging,
    ],
  );

  const updateDrag = useCallback(() => {
    if (!handleRef.current || !jointId) {
      return;
    }

    const nextTransform = getObjectTransform(handleRef.current);
    setShadowTransform(nextTransform);
    if (!ensureDragStarted(nextTransform)) {
      return;
    }

    solveAtQuaternion(quaternionFromTuple(nextTransform.quaternion));
  }, [ensureDragStarted, jointId, solveAtQuaternion]);

  const finishDrag = useCallback(() => {
    if (!isDraggingRef.current) {
      clearPendingHandleDrag();
      releasePendingHandleCamera();
      return;
    }

    const timing = createKeyPoseTimingLogger('fk.finishDrag', {
      jointId,
      keyPoseId: dragBaseKeyPoseRef.current?.id ?? keyPose?.id ?? null,
      lockFeet,
    });

    try {
      if (cameraControls && 'enabled' in cameraControls) {
        (cameraControls as { enabled: boolean }).enabled = false;
        cameraControls.cancel?.();
        cameraControls.stop?.();
      }
      clearKeyPoseDragPreview();
      editableSceneRef.current = null;
      const baseKeyPose = dragBaseKeyPoseRef.current ?? keyPose;
      const nextQuaternion = handleRef.current
        ? getObjectQuaternion(handleRef.current)
        : null;
      const usedCachedSolution = latestSolvedRef.current !== null;
      const solved =
        latestSolvedRef.current ??
        (nextQuaternion ? solveAtQuaternion(nextQuaternion) : null);
      timing.mark('resolved final solve', {
        hasBaseKeyPose: Boolean(baseKeyPose),
        hasNextQuaternion: Boolean(nextQuaternion),
        hasSolvedPose: Boolean(solved),
        usedCachedSolution,
      });

      if (!baseKeyPose || !jointId || !nextQuaternion || !solved) {
        if (baseKeyPose) {
          keyPoseStore.clearKeyPoseEditingPreview(baseKeyPose.id);
        }
        timing.mark('aborted finish');
        return;
      }

      const beforeAction = dragCreatedKeyPoseRef.current
        ? { keyPose: null, ikTargetPositions: null }
        : keyPoseStore.captureKeyPoseActionSnapshot(baseKeyPose.id);
      timing.mark('captured undo snapshot');
      const result = keyPoseStore.updateKeyPose({
        keyPoseId: baseKeyPose.id,
        ...solved,
      });
      timing.mark('updated key pose', {
        didWrite: Boolean(result?.didWrite),
        resultKeyPoseId: result?.keyPoseId ?? null,
      });
      if (!result?.didWrite) {
        keyPoseStore.clearKeyPoseEditingPreview(baseKeyPose.id);
        return;
      }

      const updatedKeyPose =
        keyPoseStoreApi.state.keyPosesById[result.keyPoseId];
      const targetPositions =
        keyPoseStoreApi.state.editing.ikTargetPositionsByKeyPoseId[
          result.keyPoseId
        ];
      if (updatedKeyPose && targetPositions) {
        const updatedScene = createEditableKeyPoseScene({
          actorScene,
          keyPose: updatedKeyPose,
          sceneRotation,
        });

        if (updatedScene) {
          for (const effectorId of Object.keys(
            targetPositions,
          ) as KeyPoseIkEffectorId[]) {
            const position = getKeyPoseIkEffectorWorldPosition(
              updatedScene,
              effectorId,
            );
            if (position) {
              keyPoseStoreApi.setKeyPoseIkTargetPosition({
                keyPoseId: result.keyPoseId,
                effectorId,
                position,
              });
            }
          }
        }
      }
      timing.mark('refreshed stored IK targets after FK edit', {
        targetCount: targetPositions ? Object.keys(targetPositions).length : 0,
      });

      keyPoseStore.clearKeyPoseEditingPreview(result.keyPoseId);
      if (baseKeyPose.id !== result.keyPoseId) {
        keyPoseStore.clearKeyPoseEditingPreview(baseKeyPose.id);
      }
      timing.mark('cleared editing previews');
      keyPoseStore.recordKeyPoseEditAction({
        keyPoseId: result.keyPoseId,
        referenceName: result.referenceName,
        label: 'Edit key pose',
        before: beforeAction,
      });
      timing.mark('recorded edit action');
      void saveCurrentKeyPoses('edit-key-pose', {
        referenceName: result.referenceName,
      });
      timing.mark('queued key-pose save');
      reconcileControlFramesAfterEdit();
      timing.mark('queued control-frame reconcile');
    } finally {
      clearPendingHandleDrag();
      dragBaseKeyPoseRef.current = null;
      dragStartQuaternionRef.current = null;
      dragPreviousTargetQuaternionRef.current = null;
      dragPinnedAnklePositionsRef.current = null;
      dragCreatedKeyPoseRef.current = false;
      latestSolvedRef.current = null;
      setDragging(false);
      timing.end('released dragging');
    }
  }, [
    actorScene,
    cameraControls,
    clearPendingHandleDrag,
    jointId,
    keyPose,
    keyPoseStore,
    keyPoseStoreApi,
    reconcileControlFramesAfterEdit,
    releasePendingHandleCamera,
    saveCurrentKeyPoses,
    sceneRotation,
    setDragging,
    solveAtQuaternion,
  ]);

  if (!actorScene || !sourceBvh || !keyPose || !jointId || !shadowTransform) {
    return null;
  }

  return (
    <group>
      <group
        ref={setHandleRefs}
        position={shadowTransform.position}
        onPointerDown={(event) => {
          event.stopPropagation();
        }}
      />
      {handleObject ? (
        <TransformControls
          object={handleObject}
          mode="rotate"
          size={KEY_POSE_TRANSFORM_CONTROLS_SIZE}
          onMouseDown={startPendingDrag}
          onMouseUp={finishDrag}
          onObjectChange={updateDrag}
        />
      ) : null}
    </group>
  );
}

function clonePosition(
  position: readonly [number, number, number],
): KeyPoseVector3Tuple {
  return [position[0], position[1], position[2]];
}

function areFkTransformsEqual(
  left: KeyPoseFkShadowTransform,
  right: KeyPoseFkShadowTransform,
) {
  return (
    areVector3TuplesEqual(left.position, right.position) &&
    areQuaternionTuplesEqual(left.quaternion, right.quaternion)
  );
}

function areVector3TuplesEqual(
  left: KeyPoseVector3Tuple,
  right: KeyPoseVector3Tuple,
) {
  return (
    areNumbersEqual(left[0], right[0], GIZMO_POSITION_EPSILON) &&
    areNumbersEqual(left[1], right[1], GIZMO_POSITION_EPSILON) &&
    areNumbersEqual(left[2], right[2], GIZMO_POSITION_EPSILON)
  );
}

function areQuaternionTuplesEqual(
  left: KeyPoseQuaternionTuple,
  right: KeyPoseQuaternionTuple,
) {
  return (
    areNumbersEqual(left[0], right[0], GIZMO_ROTATION_EPSILON) &&
    areNumbersEqual(left[1], right[1], GIZMO_ROTATION_EPSILON) &&
    areNumbersEqual(left[2], right[2], GIZMO_ROTATION_EPSILON) &&
    areNumbersEqual(left[3], right[3], GIZMO_ROTATION_EPSILON)
  );
}

function areNumbersEqual(left: number, right: number, epsilon: number) {
  return Math.abs(left - right) <= epsilon;
}

function cloneQuaternionTuple(
  quaternion: readonly [number, number, number, number],
): KeyPoseQuaternionTuple {
  return [quaternion[0], quaternion[1], quaternion[2], quaternion[3]];
}

function applyShadowTransform(
  object: Object3D,
  transform: KeyPoseFkShadowTransform,
) {
  object.position.set(
    transform.position[0],
    transform.position[1],
    transform.position[2],
  );
  object.quaternion.copy(quaternionFromTuple(transform.quaternion));
}

function getObjectTransform(object: Object3D): KeyPoseFkShadowTransform {
  return {
    position: [object.position.x, object.position.y, object.position.z],
    quaternion: getObjectQuaternionTuple(object),
  };
}

function getObjectQuaternion(object: Object3D) {
  return object.quaternion.clone().normalize();
}

function getObjectQuaternionTuple(object: Object3D): KeyPoseQuaternionTuple {
  const quaternion = getObjectQuaternion(object);
  return [quaternion.x, quaternion.y, quaternion.z, quaternion.w];
}

function quaternionFromTuple(
  quaternion: readonly [number, number, number, number],
) {
  return new Quaternion(
    quaternion[0],
    quaternion[1],
    quaternion[2],
    quaternion[3],
  ).normalize();
}
